import {createFragmentContainer, graphql} from 'react-relay';
import {getCurrency} from '../util/BudgetUtils';
import Util from '../../../../../forecast-app/shared/util/util';

const BudgetTotalsLoader = ({viewer, children}) => {
	const rows = [];
	const currency = getCurrency(viewer.company, viewer.project);
	const {financialNumbers, billable, budget} = viewer.project;
	const hasLimitedFinancialAccess = Util.hasLimitedFinancialAccess();

	if (billable) {
		const revenueData = [
			{
				type: 'fixedPriceRevenue',
				value: budget,
			},
			{
				type: 'baselineRevenue',
				value: financialNumbers.baselineRevenue,
			},
			{
				type: 'baselineVsActualRevenue',
				value: financialNumbers.baselineVsActualRevenue,
			},
			{
				type: 'baselineVsForecastRevenue',
				value: financialNumbers.baselineVsForecastRevenue,
			},
			{
				type: 'actualRevenue',
				value: financialNumbers.actualRevenue,
			},
			{
				type: 'plannedRevenue',
				value: financialNumbers.plannedRevenue,
			},
			{
				type: 'actualVsPlanRevenue',
				value: financialNumbers.actualVsPlanRevenue,
			},
			{
				type: 'forecastVsPlanRevenue',
				value: financialNumbers.forecastVsPlanRevenue,
			},
			{
				type: 'remainingRevenue',
				value: financialNumbers.remainingRevenue,
			},
			{
				type: 'forecastRevenue',
				value: financialNumbers.forecastRevenue,
			},
		];

		const revenueRow = {
			dataArray: revenueData,
			currency: currency,
			rowType: 'revenue',
		};
		rows.push(revenueRow);
	}

	const costData = [
		{
			type: 'fixedPriceCost',
			value: financialNumbers.forecastCost,
		},
		{
			type: 'baselineCost',
			value: financialNumbers.baselineCost,
		},
		{
			type: 'baselineVsActualCost',
			value: financialNumbers.baselineVsActualCost,
		},
		{
			type: 'baselineVsForecastCost',
			value: financialNumbers.baselineVsForecastCost,
		},
		{
			type: 'actualCost',
			value: financialNumbers.actualCost,
		},
		{
			type: 'plannedCost',
			value: financialNumbers.plannedCost,
		},
		{
			type: 'actualVsPlanCost',
			value: financialNumbers.actualVsPlanCost,
		},
		{
			type: 'forecastVsPlanCost',
			value: financialNumbers.forecastVsPlanCost,
		},
		{
			type: 'remainingCost',
			value: financialNumbers.remainingCost,
		},
		{
			type: 'forecastCost',
			value: financialNumbers.forecastCost,
		},
	];

	const costRow = {
		dataArray: costData,
		currency: currency,
		rowType: 'cost',
	};
	if (!hasLimitedFinancialAccess) {
		rows.push(costRow);
	}

	if (billable) {
		const profitData = [
			{
				type: 'fixedPriceProfit',
				value: budget - financialNumbers.forecastCost,
			},
			{
				type: 'baselineProfit',
				value: financialNumbers.baselineProfit,
			},
			{
				type: 'baselineVsActualProfit',
				value: financialNumbers.baselineVsActualProfit,
			},
			{
				type: 'baselineVsForecastProfit',
				value: financialNumbers.baselineVsForecastProfit,
			},
			{
				type: 'actualProfit',
				value: financialNumbers.actualProfit,
			},
			{
				type: 'plannedProfit',
				value: financialNumbers.plannedProfit,
			},
			{
				type: 'actualVsPlanProfit',
				value: financialNumbers.actualVsPlanProfit,
			},
			{
				type: 'forecastVsPlanProfit',
				value: financialNumbers.forecastVsPlanProfit,
			},
			{
				type: 'remainingProfit',
				value: financialNumbers.remainingProfit,
			},
			{
				type: 'forecastProfit',
				value: financialNumbers.forecastProfit,
			},
		];

		const profitRow = {
			dataArray: profitData,
			currency: currency,
			rowType: 'profit',
		};
		if (!hasLimitedFinancialAccess) {
			rows.push(profitRow);
		}
	}

	if (billable) {
		const marginData = [
			{
				type: 'fixedPriceMargin',
				value: (budget - financialNumbers.forecastCost) / budget,
			},
			{
				type: 'baselineMargin',
				value: financialNumbers.baselineMargin,
			},
			{value: null},
			{value: null},
			{
				type: 'actualMargin',
				value: financialNumbers.actualMargin,
			},
			{
				type: 'plannedMargin',
				value: financialNumbers.plannedMargin,
			},
			{value: null},
			{value: null},
			{
				type: 'remainingMargin',
				value: financialNumbers.remainingMargin,
			},
			{
				type: 'forecastMargin',
				value: financialNumbers.forecastMargin,
			},
		];

		const marginRow = {
			dataArray: marginData,
			currency: currency,
			rowType: 'margin',
		};
		if (!hasLimitedFinancialAccess) {
			rows.push(marginRow);
		}
	}

	const result = {data: {rows: [...rows]}};

	return children(result);
};
export const budgetTotalsLoaderQuery = graphql`
	query BudgetTotalsLoader_Query(
		$projectId: ID!
		$startYear: Int
		$startMonth: Int
		$startDay: Int
		$endYear: Int
		$endMonth: Int
		$endDay: Int
	) {
		viewer {
			actualPersonId
			component(name: "project_budget_v3_totals_loader")
			...BudgetTotalsLoader_viewer
				@arguments(
					projectId: $projectId
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
				)
		}
	}
`;

export default createFragmentContainer(BudgetTotalsLoader, {
	viewer: graphql`
		fragment BudgetTotalsLoader_viewer on Viewer
		@argumentDefinitions(
			projectId: {type: "ID!"}
			startYear: {type: "Int"}
			startMonth: {type: "Int"}
			startDay: {type: "Int"}
			endYear: {type: "Int"}
			endMonth: {type: "Int"}
			endDay: {type: "Int"}
		) {
			id
			company {
				currency
			}
			project(internalId: $projectId) {
				# Fixed price v1
				budget

				rateCard {
					currency
				}
				billable
				useBaseline
				financialNumbers(
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					convertToProjectCurrency: true
				) {
					# Revenue
					baselineRevenue
					baselineVsActualRevenue
					baselineVsForecastRevenue
					actualRevenue
					plannedRevenue
					actualVsPlanRevenue
					forecastVsPlanRevenue
					remainingRevenue
					forecastRevenue

					# Cost
					baselineCost
					baselineVsActualCost
					baselineVsForecastCost
					actualCost
					plannedCost
					actualVsPlanCost
					forecastVsPlanCost
					remainingCost
					forecastCost

					# Profit
					baselineProfit
					baselineVsActualProfit
					baselineVsForecastProfit
					actualProfit
					plannedProfit
					actualVsPlanProfit
					forecastVsPlanProfit
					remainingProfit
					forecastProfit

					# Margin
					baselineMargin
					actualMargin
					plannedMargin
					remainingMargin
					forecastMargin
				}
			}
		}
	`,
});
